import { Alert, Box, CircularProgress } from '@mui/material';
import React from 'react';
import { AssociationCompleteStep } from '../../InvestmentEntity/AssociationCompleteStep';
import { CreateEntityStep } from '../../InvestmentEntity/CreateEntityStep';
import { EntityAllocationStep } from '../../InvestmentEntity/EntityAllocationStep';
import { EntityLookupStep } from '../../InvestmentEntity/EntityLookupStep';
import { EntitySelectionStep } from '../../InvestmentEntity/EntitySelectionStep';
import { ContactAssociationStep } from 'ContactAssociationStep';
import { AdminEntityAssociationWizardState } from './adminEntityAssociationWizardTypes';
import { remainingCommitment, sumAllocations } from './adminEntityAssociationWizardUtils';

interface AdminEntityAssociationWizardStepsProps {
  state: AdminEntityAssociationWizardState;
  onLookupComplete: () => void;
  onEntitySelected: (entity: AdminEntityAssociationWizardState['entities'][number]) => void;
  onCreateNew: () => void;
  onEntityCreated: (
    entityData: NonNullable<AdminEntityAssociationWizardState['newEntity']>,
  ) => Promise<void>;
  onCreateCancel: () => void;
  onAllocationComplete: (allocatedAmount: number) => void;
  onAllocationBack: () => void;
  onContactsComplete: () => void;
  onComplete: () => void;
}

export const AdminEntityAssociationWizardSteps: React.FC<
  AdminEntityAssociationWizardStepsProps
> = ({
  state,
  onLookupComplete,
  onEntitySelected,
  onCreateNew,
  onEntityCreated,
  onCreateCancel,
  onAllocationComplete,
  onAllocationBack,
  onContactsComplete,
  onComplete,
}) => {
  const allocations = state.currentAllocations || [];
  const totalAllocations = sumAllocations(allocations);
  const remainingAllocations = remainingCommitment(state.investor, allocations);

  if (state.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (state.error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {state.error}
      </Alert>
    );
  }

  switch (state.currentStep) {
    case 'lookup':
      return <EntityLookupStep onComplete={onLookupComplete} />;

    case 'selection':
      return (
        <EntitySelectionStep
          entities={state.entities}
          onEntitySelected={onEntitySelected}
          onCreateNew={onCreateNew}
          currentAllocations={allocations}
          totalAllocations={totalAllocations}
          remainingAllocations={remainingAllocations}
        />
      );

    case 'create':
      return (
        <CreateEntityStep
          onEntityCreated={onEntityCreated}
          initialData={state.newEntity || undefined}
          onCancel={onCreateCancel}
          validationErrors={state.validationErrors}
        />
      );

    case 'allocation':
      if (!state.selectedEntity || !state.investor) return null;
      return (
        <EntityAllocationStep
          entity={state.selectedEntity}
          investor={state.investor}
          onNext={onAllocationComplete}
          onBack={onAllocationBack}
          loading={state.loading}
          validationErrors={state.validationErrors}
          currentAllocations={state.currentAllocations}
        />
      );

    case 'contacts':
      if (!state.selectedEntity) return null;
      return (
        <ContactAssociationStep
          entityId={state.selectedEntity.id}
          entityName={state.selectedEntity.name}
          entityType={state.selectedEntity.type}
          onComplete={onContactsComplete}
        />
      );

    case 'complete':
      return (
        <AssociationCompleteStep
          entity={state.selectedEntity}
          investor={state.investor}
          onComplete={onComplete}
        />
      );

    default:
      return null;
  }
};
